"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { Button } from "@/components/ui-system";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { isAppHttpError } from "@/lib/api/client";
import { DEFAULT_THEME_ACCENT, normalizeAccentColor } from "@/lib/theme/accent-color";
import { getMyProfile, updateMyProfile } from "@/lib/api/endpoints/users";
import type { UserDto } from "@/lib/api/types/enterprise";
import { useAuthStore } from "@/modules/auth/store";
import { ProfileThemeSettings } from "@/modules/profile/components/profile-theme-settings";
import { useThemeStore, type ThemeMode } from "@/store/theme-store";

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

function authUserFromDto(profile: UserDto, currentTenantId: string | null) {
  return {
    id: profile.id,
    email: profile.email,
    name: profile.name,
    tenantId: currentTenantId ?? profile.tenant_id,
    role: profile.role,
    organizationId: profile.organization_id,
    organizationName: profile.organization_name,
    tenants: profile.tenants.map((tenant) => ({
      id: tenant.id,
      name: tenant.name,
      domain: tenant.domain,
      is_active: tenant.is_active,
    })),
  };
}

export function ProfilePage() {
  const user = useAuthStore((state) => state.user);
  const tenantId = useAuthStore((state) => state.tenantId);
  const setUser = useAuthStore((state) => state.setUser);
  const [name, setName] = useState(user?.name ?? "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const themeMode = useThemeStore((s) => s.theme);
  const accentColor = useThemeStore((s) => s.accentColor);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setAccentColor = useThemeStore((s) => s.setAccentColor);

  const profileQuery = useQuery({
    queryKey: ["my-profile"],
    queryFn: getMyProfile,
    enabled: Boolean(user),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const currentName = (profileQuery.data?.name ?? user?.name ?? "").trim();

  useEffect(() => {
    if (profileQuery.data) {
      const nextAuthUser = authUserFromDto(profileQuery.data, tenantId);
      setUser(nextAuthUser);
      setName(profileQuery.data.name ?? "");
      const prefs = profileQuery.data.preferences;
      if (prefs?.theme_mode === "light" || prefs?.theme_mode === "dark") {
        setTheme(prefs.theme_mode);
      }
      setAccentColor(prefs?.theme_accent ?? DEFAULT_THEME_ACCENT);
    }
  }, [profileQuery.data, setAccentColor, setTheme, setUser, tenantId]);

  useEffect(() => {
    if (!profileQuery.data && user) {
      setName(user.name ?? "");
    }
  }, [profileQuery.data, user]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: {
        name?: string | null;
        old_password?: string;
        new_password?: string;
        confirm_new_password?: string;
        theme_mode?: ThemeMode;
        theme_accent?: string;
      } = {};

      const trimmedName = name.trim();
      if (trimmedName !== currentName) {
        payload.name = trimmedName || null;
      }

      if (isPasswordChangeRequested) {
        payload.old_password = oldPassword;
        payload.new_password = newPassword;
        payload.confirm_new_password = confirmPassword;
      }

      const savedThemeMode = profileQuery.data?.preferences?.theme_mode ?? "light";
      const savedAccent = normalizeAccentColor(profileQuery.data?.preferences?.theme_accent) ?? DEFAULT_THEME_ACCENT;
      const nextAccent = normalizeAccentColor(accentColor) ?? DEFAULT_THEME_ACCENT;
      if (themeMode !== savedThemeMode) {
        payload.theme_mode = themeMode;
      }
      if (nextAccent !== savedAccent) {
        payload.theme_accent = nextAccent;
      }

      return updateMyProfile(payload);
    },
    onSuccess: (profile) => {
      setUser(authUserFromDto(profile, tenantId));
      setName(profile.name ?? "");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFormError(null);
      setToast("Profile updated.");
    },
    onError: (error) => {
      setFormError(errorDetail(error));
    },
  });

  const hasCurrentPassword = oldPassword.trim().length > 0;
  const hasNewPassword = newPassword.trim().length > 0;
  const hasConfirmPassword = confirmPassword.trim().length > 0;
  const isPasswordChangeRequested = hasNewPassword || hasConfirmPassword;
  const isNameChanged = name.trim() !== currentName;
  const savedThemeMode = profileQuery.data?.preferences?.theme_mode ?? themeMode;
  const savedAccent = normalizeAccentColor(profileQuery.data?.preferences?.theme_accent) ?? DEFAULT_THEME_ACCENT;
  const currentAccent = normalizeAccentColor(accentColor) ?? DEFAULT_THEME_ACCENT;
  const isThemeChanged = themeMode !== savedThemeMode || currentAccent !== savedAccent;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (isPasswordChangeRequested) {
      if (!hasCurrentPassword) {
        setFormError("Current password is required to set a new password.");
        return;
      }
      if (!hasNewPassword) {
        setFormError("New password is required.");
        return;
      }
      if (!hasConfirmPassword) {
        setFormError("Please confirm the new password.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setFormError("New password and confirmation do not match.");
        return;
      }
    }

    if (!isNameChanged && !isPasswordChangeRequested && !hasCurrentPassword && !isThemeChanged) {
      setFormError("Update your name, appearance, or enter password details before saving.");
      return;
    }

    if (!isNameChanged && !isThemeChanged && hasCurrentPassword && !isPasswordChangeRequested) {
      setFormError("Enter a new password to change it, or update your name before saving.");
      return;
    }

    saveMutation.mutate();
  };

  return (
    <EnterprisePageShell
      title="Profile"
      // description="Manage your personal account details. Email stays locked, while name and password can be updated here."
    >
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}

      <form onSubmit={handleSubmit} className="grid gap-5">
        <section className="grid gap-5 rounded-card border border-card-border bg-surface-primary p-5 shadow-raised">
          <div className="grid gap-1">
            <h2 className="text-lg font-semibold text-foreground">Profile settings</h2>
            <p className="text-sm text-muted">Keep your account details current without going through role-based permissions.</p>
          </div>

          {formError ? (
            <div className="rounded-control border border-error/30 bg-error/10 px-3 py-2 text-caption text-error" role="alert">
              {formError}
            </div>
          ) : null}

          {profileQuery.isError ? (
            <div className="rounded-control border border-error/30 bg-error/10 px-3 py-2 text-caption text-error" role="alert">
              {errorDetail(profileQuery.error)}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2">
              <span className="form-label">Email</span>
              <input
                value={profileQuery.data?.email ?? user?.email ?? ""}
                readOnly
                className="ui-control h-11 bg-surface-secondary text-muted"
              />
              <span className="form-helper-disabled">Email is locked because it is used as your login identity.</span>
            </label>

            <label className="grid gap-2">
              <span className="form-label">Name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter your display name"
                className="ui-control h-11 text-foreground"
              />
              <span className="form-helper">This name appears in ownership labels, activity views, and internal lists.</span>
            </label>
          </div>
        </section>

        <ProfileThemeSettings
          themeMode={themeMode}
          accentColor={accentColor}
          onThemeModeChange={setTheme}
          onAccentColorChange={setAccentColor}
        />

        <section className="admin-surface grid gap-5 rounded-[24px] p-5">
          <div className="grid gap-1">
            <h2 className="text-lg font-semibold text-foreground">Change password</h2>
            <p className="text-sm text-muted">To set a new password, first confirm your current one.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="grid gap-2">
              <span className="form-label">Current password</span>
              <input
                type="password"
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
                autoComplete="off"
                className="ui-control h-11 text-foreground"
              />
              <span className="form-helper">Required before a new password can be saved.</span>
            </label>

            <label className="grid gap-2">
              <span className="form-label">New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                className="ui-control h-11 text-foreground"
              />
              <span className="form-helper">Choose a password you have not used here recently.</span>
            </label>

            <label className="grid gap-2">
              <span className="form-label">Confirm new password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                className="ui-control h-11 text-foreground"
              />
              <span className="form-helper">Re-enter the new password so we can catch typos before saving.</span>
            </label>
          </div>
        </section>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saveMutation.isPending || profileQuery.isLoading}
            loading={saveMutation.isPending}
            loadingText="Saving..."
          >
            Save profile
          </Button>
        </div>
      </form>
    </EnterprisePageShell>
  );
}
